import Skeleton from '../../components/layout/Skeleton';
import { useQuery } from 'react-query';
import axios from 'axios';
import HBox from '../../components/layout/HBox';
import VBox from '@/components/layout/VBox';
import { translateFilter } from './util';
import { useCallback, useEffect, useState } from 'react';
import Botao from '../../components/Botao';
import CampoSeletor from '../../components/Formulario/CampoSeletor';
import { differenceInDays } from 'date-fns';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

const INTENCOES = [
    'Suporte Técnico',
    'Informação',
    'Reclamação',
    'Cancelamento',
    'Compra/Venda',
    'Agendamento',
    'Financeiro',
    'Outros',
] as const;

const ORIGEM_OPTIONS = [
    { id: '', label: 'Todas as origens' },
    { id: 'humano', label: 'Só atendimento humano' },
    { id: 'chatbot', label: 'Só chatbot' },
];

const parseDate = (v: string) =>
    new Date(v.substring(0, 10) + 'T00:00:00.000').toLocaleDateString();

export const GraficoIntencaoPrincipal = ({ colorPalette, periodo, filterCanal }: any) => {
    const [foco, setFoco] = useState<string | null>(null);
    const [origem, setOrigem] = useState<string>('');

    const intencaoCor = (intencao: string) => {
        const i = INTENCOES.indexOf(intencao as (typeof INTENCOES)[number]);
        return colorPalette[i % colorPalette.length];
    };

    const calcBestInterval = () => {
        let d1 = periodo![1],
            d2 = periodo![2];
        if (!d2) {
            d2 = new Date().toISOString().substring(0, 10);
        }
        const days = differenceInDays(d2, d1);
        if (days <= 7) return 'dia';
        if (days <= 31) return 'semana';
        return 'mes';
    };

    const [selectedPeriod, setSelectedPeriod] = useState<'dia' | 'semana' | 'mes'>(calcBestInterval);

    useEffect(() => {
        setSelectedPeriod(calcBestInterval());
    }, [periodo]);

    const CustomTooltip = useCallback(
        ({ active, payload }: any) => {
            if (active && payload && payload.length) {
                const selectedMetric = payload.find((p: any) => foco === p.dataKey);

                return (
                    <div
                        style={{
                            backgroundColor: '#fff8',
                            border: '1px solid #ccc',
                            padding: '0.5rem',
                        }}
                    >
                        {payload?.[0]?.payload?.periodo && (
                            <p>
                                <b>{parseDate(payload[0].payload.periodo)}</b>
                            </p>
                        )}
                        {selectedMetric ? (
                            <p>
                                {selectedMetric.name}: {selectedMetric.value}
                            </p>
                        ) : (
                            payload.map((p: any) => (
                                <p key={p.dataKey}>
                                    {p.name}: {p.value}
                                </p>
                            ))
                        )}
                    </div>
                );
            }
            return null;
        },
        [foco]
    );

    const q = useQuery(
        ['intencao_principal', selectedPeriod, periodo, filterCanal, origem],
        async () => {
            const filtro: any = JSON.parse(
                translateFilter({ periodo, canal_id: filterCanal })
            );
            if (origem === 'humano') {
                filtro.atendimento_humano = ['eq', true];
            } else if (origem === 'chatbot') {
                filtro.atendimento_humano = ['eq', false];
            }
            const qs = new URLSearchParams();
            qs.append('facts', 'total');
            qs.append('dimensions', `data_criacao_${selectedPeriod},intencao_principal`);
            qs.append('filter', JSON.stringify(filtro));
            const res = await axios.get('/api/relatorios/chat_resumo?' + qs.toString());
            return res.data.result;
        },
        { refetchOnWindowFocus: false }
    );

    const dadosAbsolutos = q.data
        ? q.data.reduce((acc: any[], row: any) => {
            const periodo = row[`data_criacao_${selectedPeriod}`].substring(0, 10);
            if (!acc[acc.length - 1] || acc[acc.length - 1].periodo !== periodo) {
                const base: any = { periodo };
                INTENCOES.forEach((i) => (base[i] = 0));
                acc.push(base);
            }
            const curr = acc[acc.length - 1];
            const intencao = row.intencao_principal;
            if (intencao && Object.prototype.hasOwnProperty.call(curr, intencao)) {
                curr[intencao] = parseInt(row.total);
            }
            return acc;
        }, [])
        : [];

    const totalChats = dadosAbsolutos.reduce((sum: number, d: any) => {
        return sum + INTENCOES.reduce((s, i) => s + (d[i] || 0), 0);
    }, 0);

    // Legenda/barras ordenadas pelo volume do período (maior → menor), não pela
    // ordem fixa da constante — destaca as intenções mais frequentes à esquerda.
    const totalPorIntencao: Record<string, number> = {};
    INTENCOES.forEach(i => (totalPorIntencao[i] = 0));
    dadosAbsolutos.forEach((d: any) => INTENCOES.forEach(i => (totalPorIntencao[i] += d[i] || 0)));
    const intencoesOrdenadas = [...INTENCOES].sort(
        (a, b) => totalPorIntencao[b] - totalPorIntencao[a]
    );

    const handleMouseEnter = (o: any) => setFoco(o.dataKey);
    const handleMouseLeave = () => setFoco(null);

    if (q.isFetching) return <Skeleton format="box" />;

    const legendFormatter = (v: any, e: any) => {
        const { dataKey } = e;
        return (
            <span
                style={{
                    color: intencaoCor(dataKey),
                    opacity: !foco || foco === dataKey ? 1 : 0.3,
                    fontWeight: foco === dataKey ? 'bold' : 'normal',
                }}
            >
                {v}
            </span>
        );
    };

    return (
        <VBox>
            <HBox>
                <CampoSeletor
                    noSearch
                    placeholder="Origem"
                    options={ORIGEM_OPTIONS}
                    value={origem}
                    onChange={(value: string | null) => setOrigem(value ?? '')}
                    style={{ width: 160, flex: '0 0 160px' }}
                />
                <Botao
                    variant={selectedPeriod === 'dia' ? 'outline-primary' : 'outline-secondary'}
                    onClick={() => setSelectedPeriod('dia')}
                >
                    Dia
                </Botao>
                <Botao
                    variant={selectedPeriod === 'semana' ? 'outline-primary' : 'outline-secondary'}
                    onClick={() => setSelectedPeriod('semana')}
                >
                    Semana
                </Botao>
                <Botao
                    variant={selectedPeriod === 'mes' ? 'outline-primary' : 'outline-secondary'}
                    onClick={() => setSelectedPeriod('mes')}
                >
                    Mês
                </Botao>
            </HBox>

            {totalChats < 10 && (
                <VBox center>
                    <p
                        style={{
                            fontSize: '0.875rem',
                            color: 'var(--tc-color-gray-500)',
                            textAlign: 'center',
                        }}
                    >
                        Sem dados suficientes para o período selecionado.
                    </p>
                </VBox>
            )}

            {dadosAbsolutos.length === 0 && (
                <VBox center>
                    <p>Nenhum dado encontrado</p>
                </VBox>
            )}

            {dadosAbsolutos.length === 1 && (
                <>
                    <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart margin={{ top: 5, right: 5, left: 5, bottom: 5 }} data={dadosAbsolutos}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="periodo" tickFormatter={parseDate} />
                                <YAxis allowDecimals={false} />
                                <Tooltip
                                    cursor={{ fill: '#8882' }}
                                    itemSorter={(item: any) => -item.value as number}
                                    itemStyle={{ padding: 0, fontSize: '0.9rem' }}
                                    labelStyle={{ fontWeight: 'bold' }}
                                    content={<CustomTooltip />}
                                />
                                <Legend
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                    wrapperStyle={{ height: '60px', overflowY: 'auto' }}
                                    height={80}
                                    formatter={legendFormatter}
                                />
                                {intencoesOrdenadas.map((intencao) => (
                                    <Bar
                                        key={intencao}
                                        dataKey={intencao}
                                        name={intencao}
                                        fill={intencaoCor(intencao)}
                                        maxBarSize={50}
                                        opacity={!foco || foco === intencao ? 1 : 0.2}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <p
                        style={{
                            fontSize: '0.75rem',
                            fontStyle: 'italic',
                            color: 'var(--tc-color-gray-500)',
                            textAlign: 'center',
                        }}
                    >
                        Intenção principal identificada por IA. Consulte as conversas do
                        período para mais detalhes.
                    </p>
                </>
            )}

            {dadosAbsolutos.length > 1 && (
                <>
                    <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart margin={{ top: 5, right: 5, left: 5, bottom: 5 }} data={dadosAbsolutos}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="periodo" tickFormatter={parseDate} />
                                <YAxis allowDecimals={false} />
                                <Tooltip
                                    cursor={{ fill: '#8882' }}
                                    itemSorter={(item: any) => -item.value as number}
                                    itemStyle={{ padding: 0, fontSize: '0.9rem' }}
                                    labelStyle={{ fontWeight: 'bold' }}
                                    content={<CustomTooltip />}
                                />
                                <Legend
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                    wrapperStyle={{ height: '60px', overflowY: 'auto' }}
                                    height={80}
                                    formatter={legendFormatter}
                                />
                                {intencoesOrdenadas.map((intencao) => (
                                    <Line
                                        key={intencao}
                                        type="monotone"
                                        dataKey={intencao}
                                        name={intencao}
                                        stroke={intencaoCor(intencao)}
                                        strokeOpacity={!foco || foco === intencao ? 1 : 0.2}
                                        strokeDasharray={!foco || foco === intencao ? '0' : '5 5'}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    <p
                        style={{
                            fontSize: '0.75rem',
                            fontStyle: 'italic',
                            color: 'var(--tc-color-gray-500)',
                            textAlign: 'center',
                        }}
                    >
                        Intenção principal identificada por IA. Consulte as conversas do
                        período para mais detalhes.
                    </p>
                </>
            )}
        </VBox>
    )
};
